import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { PHONE_DISPLAY, telLink, whatsappLink, EMAIL, ADDRESS } from "@/lib/whatsapp";

export default function Contact() {
  return (
    <section id="contact" className="bg-brand-ink text-white py-24 md:py-32 overflow-hidden" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-12 gap-8 md:gap-14">
          <div className="md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-orange">Chapter IX · Get in touch</div>
            <h2 className="mt-4 font-display font-black text-5xl md:text-6xl leading-[1.02]">
              Let's plan <br /><span className="italic text-brand-orange font-medium">your journey.</span>
            </h2>
            <p className="mt-6 text-white/70 max-w-md">Reach us any hour — for a five-minute city ride or a two-week Rajasthan expedition. We answer every call.</p>

            <div className="mt-10 space-y-4">
              <ContactLink icon={Phone} label="Call us anytime" value={PHONE_DISPLAY} href={telLink()} testid="contact-call" />
              <ContactLink icon={WhatsAppIcon} label="WhatsApp us" value={PHONE_DISPLAY} href={whatsappLink("Hi Mamta Tours!")} testid="contact-whatsapp" external />
              <ContactLink icon={Mail} label="Email us" value={EMAIL} href={`mailto:${EMAIL}`} testid="contact-email" />
              <div className="flex items-start gap-4 rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="w-11 h-11 rounded-xl bg-brand-orange/20 text-brand-orange flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-white/60">Office Address</div>
                  <div className="mt-1 text-white text-sm leading-relaxed">{ADDRESS}</div>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-7"
          >
            <div className="rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]">
              <iframe
                title="Mamta Tours & Travels — Google Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3578.789822484702!2d72.99597577572007!3d26.23601791789991!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39418bc94c0c64e5%3A0xcd242b014fad54e4!2sMamta%20tours%20and%20travels!5e0!3m2!1sen!2sin!4v1784909847522!5m2!1sen!2sin"
                width="100%"
                height="520"
                style={{ border: 0, filter: "grayscale(0.15) contrast(0.95)" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                data-testid="contact-map"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ContactLink({ icon: Icon, label, value, href, testid, external }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="group flex items-center justify-between gap-4 rounded-2xl bg-white/5 border border-white/10 p-5 hover:bg-brand-blue transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-brand-blue text-white flex items-center justify-center group-hover:bg-brand-orange transition-colors"><Icon className="w-5 h-5" /></div>
        <div>
          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-white/60">{label}</div>
          <div className="mt-0.5 text-white font-semibold">{value}</div>
        </div>
      </div>
      <ArrowUpRight className="w-4 h-4 text-white/60 group-hover:text-white transition-colors" />
    </a>
  );
}
